import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./skillSidebar.html";
import { aiAssetEngagement } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { CookieConsentCategory, createCatCookie, getCookieConsented } from '~/modules/utilities/cookies';
import { getExternalInstallConfig } from '~/modules/utilities/external_install_utils';
import { buildSkillsInstallCommand, getSkillSlug, getSkillSource, isPrivateSkillSource, getSkillSupportedHarnesses, getSkillInstallOptions } from '~/modules/utilities/skill_utils';
const DEFAULT_INSTALL_METHOD_ID = 'claude-code';
const INSTALL_METHOD_IDS = ['agentforce-vibes', 'claude-code', 'cursor', 'codex', 'opencode'];

// The install options a harness can offer, in primary-CTA precedence: a published ZIP is the
// primary action when present, otherwise the install prompt, otherwise the CLI script. Claude Code's
// ZIP can also be added through Claude Desktop, always a secondary option behind the ZIP itself.

const INSTALL_OPTION_TABS = {
  zip: 'ZIP Download',
  prompt: 'AI Prompt',
  cli: 'CLI Script',
  desktop: 'ZIP Download'
};

// One preference applies to every skill listing. As a non-essential convenience setting it follows
// the package-contents view-mode precedent: Functional consent, a 30-day lifetime, and the shared
// cookie utility's root path / SameSite=Lax / HTTPS-only Secure attributes.
const INSTALL_METHOD_COOKIE_NAME = 'appx_skill_install_method';
const INSTALL_METHOD_COOKIE_EXPIRY_DAYS = 30;
function isInstallMethodId(value) {
  return typeof value === 'string' && INSTALL_METHOD_IDS.some(installMethodId => installMethodId === value);
}

// Field initializers can run during SSR, where browser globals do not exist. Claude Code remains the
// safe default when no consented cookie exists or a stale/tampered cookie contains an unknown value.
function readInitialInstallMethodId() {
  if (typeof window === 'undefined') {
    return DEFAULT_INSTALL_METHOD_ID;
  }
  const savedInstallMethodId = getCookieConsented(INSTALL_METHOD_COOKIE_NAME, CookieConsentCategory.Functional);
  return isInstallMethodId(savedInstallMethodId) ? savedInstallMethodId : DEFAULT_INSTALL_METHOD_ID;
}
class SkillSidebar extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    this.labels = void 0;
    // Restore one global preference across skill listings. Invalid/missing/unconsented values fall
    // back to Claude Code; a preference the current listing doesn't support falls back further in
    // `selectedHarness`.
    this.selectedInstallMethodId = readInitialInstallMethodId();
    // The secondary install options sit behind a "More options" disclosure, collapsed by default.
    // Initialized (not merely declared) so re-assigning it re-renders under the client build.
    this.moreOptionsExpanded = false;
  }
  get installationMethodId() {
    return this.effectiveInstallMethodId;
  }
  set installationMethodId(value) {
    if (isInstallMethodId(value)) {
      this.selectedInstallMethodId = value;
    }
  }
  renderedCallback() {
    const isExternalInstall = this.isExternalInstall;
    if (this.announcedListing === this.listing && this.announcedExternalInstall === isExternalInstall) {
      return;
    }
    this.announcedListing = this.listing;
    this.announcedExternalInstall = isExternalInstall;
    if (!isExternalInstall) {
      this.dispatchInstallStateChange();
    }
  }
  get harnesses() {
    return getSkillSupportedHarnesses(this.listing);
  }

  // The dropdown only offers the harnesses this listing publishes, so the persisted preference may
  // not apply here: fall back to Claude Code when supported, then to the first published harness.
  get selectedHarness() {
    const harnesses = this.harnesses;
    return harnesses.find(harness => harness.installMethodId === this.selectedInstallMethodId) ?? harnesses.find(harness => harness.installMethodId === DEFAULT_INSTALL_METHOD_ID) ?? harnesses[0];
  }
  get effectiveInstallMethodId() {
    const installMethodId = this.selectedHarness?.installMethodId;
    return isInstallMethodId(installMethodId) ? installMethodId : DEFAULT_INSTALL_METHOD_ID;
  }
  get hasMultipleHarnesses() {
    return this.harnesses.length > 1;
  }
  get installMethodOptions() {
    const labels = {
      'agentforce-vibes': this.labels?.installMethodAgentforceVibes,
      'claude-code': this.labels?.installMethodClaude,
      cursor: this.labels?.installMethodCursor,
      codex: this.labels?.installMethodChatGpt,
      opencode: this.labels?.installMethodOpenCode
    };
    return this.harnesses.filter(harness => isInstallMethodId(harness.installMethodId)).map(harness => ({
      id: harness.installMethodId,
      label: labels[harness.installMethodId] ?? ''
    }));
  }
  get installForLabel() {
    return this.labels?.installFor ?? '';
  }
  get getInstallPromptLabel() {
    return this.labels?.getInstallPrompt ?? '';
  }
  get cliScriptLabel() {
    return this.labels?.commandLineTab ?? '';
  }
  get addToClaudeDesktopLabel() {
    return this.labels?.addToClaudeDesktop ?? '';
  }
  get downloadZipLabel() {
    return this.labels?.downloadZip ?? '';
  }
  get viewOnGitHubLabel() {
    return this.labels?.viewOnGitHub ?? '';
  }
  get moreOptionsLabel() {
    return (this.moreOptionsExpanded ? this.labels?.lessOptions : this.labels?.moreOptions) ?? '';
  }

  /** Hides (rather than unmounts) the More options panel so the toggle's `aria-controls` always
   *  resolves to a present element. */
  get moreOptionsCollapsed() {
    return !this.moreOptionsExpanded;
  }
  get moreOptionsIcon() {
    return this.moreOptionsExpanded ? 'chevronup' : 'chevrondown';
  }
  get moreOptionsAriaExpanded() {
    return this.moreOptionsExpanded ? 'true' : 'false';
  }

  /** A single-harness skill has nothing to choose, so the dropdown is hidden and the heading names
   *  the harness instead. `{HARNESS}` needs the raw `i18nStore` proxy; the wired `labels` proxy
   *  always calls message functions with no arguments. */
  get headingLabel() {
    if (this.isExternalInstall) {
      return this.labels?.installSkillHeading ?? '';
    }
    if (this.harnesses.length === 1) {
      const formatter = i18nStore.getState().i18n?.skills?.installForHarnessHeading;
      return formatter?.({
        HARNESS: this.installMethodOptions[0]?.label ?? ''
      }) ?? '';
    }
    return this.labels?.installSkillHeading ?? '';
  }

  /** Harness whose icon leads the single-harness "Install for {HARNESS}" heading; '' otherwise. */
  get singleHarnessId() {
    if (this.isExternalInstall || this.harnesses.length !== 1) {
      return '';
    }
    return this.installMethodOptions[0]?.id ?? '';
  }
  get isExternalInstall() {
    return getExternalInstallConfig(this.listing).isExternal;
  }

  // The skill's canonical slug, used to build the `npx skills add` command — the basename of the
  // source folder path (e.g. 'agentforce-generate'), NOT the listing's `name`, which is the
  // human-readable display title ('Agentforce Generate') and not a valid CLI slug.
  get skillName() {
    return getSkillSlug(this.listing);
  }

  // Returns true when the selected harness is Agentforce Vibes (auto-installed, no command).
  get isAutoInstalled() {
    return this.effectiveInstallMethodId === 'agentforce-vibes';
  }

  // The skill's GitHub source URL, for the "View on GitHub" link. '' when the listing carries no
  // parseable source or the repo is private, in which case the link is hidden (see hasSourceUrl).
  get sourceUrl() {
    const source = getSkillSource(this.listing);
    return source && !isPrivateSkillSource(source) ? source.url : '';
  }
  get hasSourceUrl() {
    return !!this.sourceUrl;
  }

  // The real install command for CLI harnesses; '' for Agentforce Vibes. The CLI harness ids
  // (claude-code, cursor, codex, opencode) ARE the -a slug values. The source must resolve to the
  // listing's immutable commit SHA; a missing or moving ref yields no command, which hides both
  // the prompt and CLI options (the prompt embeds the same command).
  get installCommand() {
    if (this.isAutoInstalled) {
      return '';
    }
    return buildSkillsInstallCommand(getSkillSource(this.listing), this.skillName, this.effectiveInstallMethodId);
  }
  get zipUrl() {
    return this.selectedHarness?.zipUrl && this.listing?.external_id ? `/api/skills/download/${encodeURIComponent(this.listing.external_id)}?harness=${encodeURIComponent(this.effectiveInstallMethodId)}` : '';
  }

  // The selected harness's published install options, in primary-CTA precedence.
  get installOptions() {
    return getSkillInstallOptions(this.listing, this.effectiveInstallMethodId);
  }
  get primaryOption() {
    return this.installOptions[0];
  }
  get secondaryOptions() {
    return this.installOptions.slice(1);
  }
  get isPrimaryZip() {
    return this.primaryOption === 'zip';
  }
  get isPrimaryPrompt() {
    return this.primaryOption === 'prompt';
  }
  get isPrimaryCli() {
    return this.primaryOption === 'cli';
  }
  get hasSecondaryOptions() {
    return this.secondaryOptions.length > 0;
  }
  get hasSecondaryPrompt() {
    return this.secondaryOptions.includes('prompt');
  }
  get hasSecondaryCli() {
    return this.secondaryOptions.includes('cli');
  }
  get hasSecondaryDesktop() {
    return this.secondaryOptions.includes('desktop');
  }

  // The install state listingDetailV2 attributes page-level engagement to: the primary option of
  // the selected harness. A card without an action has no install attribution.
  get installationTab() {
    return this.primaryOption ? INSTALL_OPTION_TABS[this.primaryOption] : undefined;
  }

  // Method selection is intentionally untracked. The private state event lets listingDetailV2
  // synchronize its inline and drawer copies and keeps later engagement payloads aligned; it is
  // not analytics.
  handleInstallMethodChange(event) {
    const selectedInstallMethodId = event.detail?.selectedId;
    // The dropdown should only emit configured option ids, but validate at this trust boundary:
    // the value is persisted and interpolated into a copyable command, so unknown values must
    // neither alter UI state nor reach the cookie/analytics payload.
    if (!isInstallMethodId(selectedInstallMethodId)) {
      return;
    }
    this.selectedInstallMethodId = selectedInstallMethodId;
    if (typeof window !== 'undefined') {
      createCatCookie(INSTALL_METHOD_COOKIE_NAME, selectedInstallMethodId, CookieConsentCategory.Functional, INSTALL_METHOD_COOKIE_EXPIRY_DAYS);
    }
    this.dispatchInstallStateChange();
  }
  handleToggleMoreOptions() {
    this.moreOptionsExpanded = !this.moreOptionsExpanded;
  }
  focusPrimaryInstallControl(options) {
    // A selector list matches in document order (the dropdown precedes the CTA), so look each
    // candidate up in priority order instead.
    const control = ['.primary-install-action', '.install-method-select'].map(selector => this.template?.querySelector(selector)).find(Boolean);
    control?.focus(options);
  }

  // Pass the trigger so wds-modal can return keyboard focus when it closes.
  handleGetInstallPrompt(event) {
    this.showModal('[data-id="install-prompt-modal"]', event);
  }
  handleCliScript(event) {
    this.showModal('[data-id="cli-script-modal"]', event);
  }
  handleAddToClaudeDesktop(event) {
    this.showModal('[data-id="claude-desktop-modal"]', event);
  }

  // The href still starts the download; the steps dialog only applies to Claude.
  handleDownloadZip(event) {
    if (this.effectiveInstallMethodId === 'claude-code') {
      this.showModal('[data-id="install-in-claude-modal"]', event);
    }
  }
  showModal(selector, event) {
    const modal = this.template?.querySelector(selector);
    modal?.show?.(event.currentTarget);
  }

  // Stamp the selected install method onto the Get Install Prompt button's auto-emitted analytics
  // event so the guided-install click carries which agent was chosen (mirrors installCommand.ts).
  handleGetInstallPromptAnalytics(event) {
    event.detail.installMethod = this.effectiveInstallMethodId;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('Get Install Prompt', 'AI Prompt');
  }
  handleCliScriptAnalytics(event) {
    event.detail.installMethod = this.effectiveInstallMethodId;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('CLI Script', 'CLI Script');
  }
  handleDownloadZipAnalytics(event) {
    event.detail.installMethod = this.effectiveInstallMethodId;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('Download ZIP', 'ZIP Download', this.zipUrl);
  }
  handleAddToClaudeDesktopAnalytics(event) {
    event.detail.installMethod = this.effectiveInstallMethodId;
    event.detail.aiAssetAnalytics = this.installOptionEngagement('Add to Claude Desktop', 'ZIP Download');
  }
  handleViewSourceAnalytics(event) {
    event.detail.aiAssetAnalytics = this.installOptionEngagement('View Source', this.installationTab, this.sourceUrl);
  }
  handleMoreOptionsAnalytics(event) {
    // wds-button emits the analytics event before the click flips `moreOptionsExpanded`, so the
    // current state describes the action the user is taking next.
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      aiAssetKind: 'skill',
      clickText: this.moreOptionsExpanded ? 'Less Options' : 'More Options',
      elementTitle: 'Install This Skill',
      elementType: 'link text',
      installationTab: this.installationTab,
      installationMethodId: this.primaryOption ? this.effectiveInstallMethodId : undefined
    });
  }
  installOptionEngagement(clickText, installationTab, clickUrl) {
    return aiAssetEngagement({
      aiAssetKind: 'skill',
      clickText,
      ...(clickUrl ? {
        clickUrl
      } : {}),
      elementTitle: 'Install This Skill',
      elementType: 'button',
      installationTab,
      installationMethodId: this.primaryOption ? this.effectiveInstallMethodId : undefined
    });
  }
  dispatchInstallStateChange() {
    this.dispatchEvent(new CustomEvent('marketplaceinstallstatechange', {
      detail: {
        installationTab: this.installationTab,
        installationMethodId: this.primaryOption ? this.effectiveInstallMethodId : undefined
      }
    }));
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SkillSidebar, {
  publicProps: {
    listing: {
      config: 0
    },
    installationMethodId: {
      config: 3
    }
  },
  publicMethods: ["focusPrimaryInstallControl"],
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["selectedInstallMethodId", "moreOptionsExpanded"]
});
const __lwc_component_class_internal = _registerComponent(SkillSidebar, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;